
import { useState, useCallback } from "react";
import { Loader2, FileText, ChevronDown, ChevronUp, Play, Check, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { NoteEditor } from "@/components/student/NoteEditor";
import { toast } from "sonner";
import { devLog } from "@/utils/devLog";

type CoursePlayerContentProps = {
  courseData: any;
  selectedLesson: any;
  setSelectedLesson: (lesson: any) => void;
  expandedModules: Set<string>;
  toggleModule: (moduleId: string) => void;
  isLessonCompleted: (lessonId: string) => boolean;
  markLessonCompleted: any;
  markLessonIncomplete: any;
  courseId: string;
  goToNextLesson: () => boolean;
  progressPercentage: number;
};

export const CoursePlayerContent = ({
  courseData,
  selectedLesson,
  setSelectedLesson,
  expandedModules,
  toggleModule,
  isLessonCompleted,
  markLessonCompleted,
  markLessonIncomplete,
  courseId,
  goToNextLesson,
  progressPercentage
}: CoursePlayerContentProps) => {
  
  const handleMarkAsCompleted = async () => {
    if (!selectedLesson) return;
    
    try {
      await markLessonCompleted.mutateAsync({ lessonId: selectedLesson.id });
      goToNextLesson();
    } catch (error) {
      console.error("Error marking lesson as completed:", error);
      toast.error("Failed to mark lesson as complete");
    }
  };

  const handleMarkAsIncomplete = async () => {
    if (!selectedLesson) return;
    
    try {
      await markLessonIncomplete.mutateAsync({ lessonId: selectedLesson.id });
    } catch (error) {
      console.error("Error marking lesson as incomplete:", error);
      toast.error("Failed to update lesson status");
    }
  };

  // Check if the current lesson is the last one in the course
  const isLastLesson = () => {
    if (!courseData?.modules || !selectedLesson) return false;
    
    let lastModuleWithLessons = null;
    let lastLessonInLastModule = null;
    
    // Find the last module that has lessons
    for (let i = courseData.modules.length - 1; i >= 0; i--) {
      if (courseData.modules[i].lessons && courseData.modules[i].lessons.length > 0) {
        lastModuleWithLessons = courseData.modules[i];
        break;
      }
    }
    
    if (lastModuleWithLessons) {
      // Get the last lesson in the last module
      lastLessonInLastModule = lastModuleWithLessons.lessons[lastModuleWithLessons.lessons.length - 1];
    }
    
    // Check if the selected lesson is the last lesson
    return lastLessonInLastModule && lastLessonInLastModule.id === selectedLesson.id;
  };

  if (!courseData) {
    return (
      <div className="flex items-center justify-center h-64 text-center">
        <div>
          <p className="text-muted-foreground">
            Course data unavailable. Please refresh and try again.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium">Course Progress</h3>
          <span className="text-sm text-muted-foreground">{progressPercentage}% Complete</span>
        </div>
        <Progress value={progressPercentage} className="h-2" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="md:col-span-1 border rounded-lg bg-muted/5 overflow-hidden">
          <div className="p-4 bg-muted/10 border-b">
            <h3 className="font-semibold text-lg">Course Curriculum</h3>
          </div>
          <div className="divide-y">
            {courseData?.modules?.map((module) => (
              <Collapsible 
                key={module.id} 
                open={expandedModules.has(module.id)}
                onOpenChange={() => toggleModule(module.id)}
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left hover:bg-muted/10">
                  <span className="font-medium">{module.title}</span>
                  {expandedModules.has(module.id) ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="bg-muted/5 divide-y">
                    {module.lessons?.map((lesson) => (
                      <button 
                        key={lesson.id}
                        onClick={() => {
                          setSelectedLesson(lesson);
                          devLog("[CoursePlayer] Lesson selected", { lessonId: lesson.id });
                        }}
                        className={`w-full text-left p-3 pl-6 hover:bg-muted/10 flex items-center gap-2 ${
                          selectedLesson?.id === lesson.id ? 'bg-primary/10 text-primary' : ''
                        }`}
                      >
                        {isLessonCompleted(lesson.id) ? (
                          <Check className="h-3.5 w-3.5 shrink-0 text-green-500" />
                        ) : selectedLesson?.id === lesson.id ? (
                          <Play className="h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <div className="h-3.5 w-3.5 shrink-0" />
                        )}
                        <span className="text-sm">{lesson.title}</span>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          {selectedLesson ? (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">{selectedLesson.title}</h2>
              
              {selectedLesson.video_url && (
                <div className="aspect-video mb-6">
                  <video 
                    src={selectedLesson.video_url} 
                    controls 
                    className="w-full h-full rounded-lg"
                    autoPlay
                  />
                </div>
              )}
              
              {selectedLesson.description && (
                <div className="prose max-w-none">
                  <h3 className="text-lg font-medium mb-2">Description</h3>
                  <p>{selectedLesson.description}</p>
                </div>
              )}
              
              {selectedLesson.lesson_files?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-2">Downloads</h3>
                  <div className="space-y-2">
                    {selectedLesson.lesson_files.map((file) => (
                      <a 
                        key={file.id} 
                        href={file.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center p-3 border rounded-md hover:bg-muted/5"
                      >
                        <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{file.file_name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedLesson.lesson_faqs?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-2">Frequently Asked Questions</h3>
                  <Accordion type="single" collapsible className="w-full">
                    {selectedLesson.lesson_faqs.map((faq, index) => (
                      <AccordionItem key={faq.id} value={`faq-${index}`}>
                        <AccordionTrigger>{faq.question}</AccordionTrigger>
                        <AccordionContent>{faq.answer}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
              
              <div className="mt-8 space-y-4">
                {isLessonCompleted(selectedLesson.id) ? (
                  <div className="space-y-4">
                    <div className="flex items-center text-green-600 font-medium">
                      <Check className="mr-2 h-5 w-5" /> Lesson Completed
                    </div>
                    <div className="flex gap-4">
                      <Button 
                        onClick={handleMarkAsIncomplete}
                        variant="outline"
                        className="flex-1"
                      >
                        Mark as Incomplete
                      </Button>
                      {!isLastLesson() && (
                        <Button 
                          onClick={goToNextLesson}
                          className="flex-1"
                        >
                          Next Lesson <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <Button 
                    onClick={handleMarkAsCompleted}
                    className="w-full"
                  >
                    <Check className="mr-2 h-4 w-4" /> Mark as Complete
                  </Button>
                )}
              </div>
              
              <div className="mt-8">
                <NoteEditor 
                  courseId={courseId} 
                  lessonId={selectedLesson.id} 
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-center">
              <div>
                <p className="text-muted-foreground">
                  Select a lesson from the curriculum to begin
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
